import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { CompanyService } from '../service/company.service';
import { Company } from '../../model';

/**
 * The company list the guard loaded, tagged with the navigation it loaded it for. The dashboard's
 * resolver runs right after the guard in the SAME navigation and would otherwise GET
 * api/company/all a second time, sequentially — the in-flight de-duplication cannot merge that.
 * Handed over at most once and only within that navigation, so it never serves stale data.
 */
@Injectable({ providedIn: 'root' })
export class GuardedCompanyList {
  private entry?: { navigationId: number; companies: Company[] };

  put(navigationId: number | undefined, companies: Company[]) {
    this.entry = navigationId === undefined ? undefined : { navigationId, companies };
  }

  take(navigationId: number | undefined): Company[] | undefined {
    const entry = this.entry;
    this.entry = undefined;
    return entry && navigationId !== undefined && entry.navigationId === navigationId ? entry.companies : undefined;
  }
}

/** Dashboard resolver: the guard's list of this navigation if there is one, else a fresh load. */
export const dashboardCompaniesResolver = async (): Promise<Company[]> => {
  const navigationId = inject(Router).currentNavigation?.()?.id;
  const guarded = inject(GuardedCompanyList).take(navigationId);
  return guarded ?? inject(CompanyService).listAll();
};

const buildDashboardLink = (company: Company) => {
  const site = ['/company', company.id, 'site', company.sites[0].id];
  if (company.enableRiskTool) {
    return site;
  }
  if (company.enableEudrTool) {
    return [...site, 'traces-eudr'];
  }
  if (company.enablePpwrTool) {
    return [...site, 'ppwr'];
  }
  return site;
};

export const dashboardRedirectGuard = async (): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const companyService = inject(CompanyService);
  const guardedList = inject(GuardedCompanyList);
  const navigationId = router.currentNavigation?.()?.id;

  try {
    const companies = await companyService.listAll();
    guardedList.put(navigationId, companies);

    // No companies available - continue to show current page
    if (!companies || companies.length === 0) {
      return true;
    }

    // If there's a last selected company, use it
    const lastSelected = companies.find(({ lastSelected }) => lastSelected);
    if (lastSelected && lastSelected.sites.length > 0) {
      const commands = buildDashboardLink(lastSelected);
      if (commands) {
        return router.createUrlTree(commands);
      }
    }

    // If there's only one company, go to its dashboard (without saving - might be read-only access)
    if (companies.length === 1 && companies[0].sites.length > 0) {
      const company = companies[0];
      const commands = buildDashboardLink(company);
      if (commands) {
        return router.createUrlTree(commands);
      }
    }

    // Multiple companies or no valid dashboard link - show dashboard with company selection
    return true;
  } catch (error) {
    console.error('Error in dashboardRedirectGuard:', error);
  }
  return true;
};
